/**
 * What each part of the application requires, as permissions.
 *
 * Still called `authRoles` because that is the name every route config and navigation
 * item imports, and renaming it would touch twenty files to say the same thing. The
 * contents are permissions now: the strings the API grants a role, listed in
 * `app/constants/permissionCatalogue.js` on that side.
 *
 * Fuse reads these through `FuseUtils.hasPermission`, which asks whether the account
 * holds **any** of them — so a list here means "any one of these will do", never "all
 * of them". A screen needing two at once has to ask in the component.
 *
 * The old entries mapped to the job titles that happened to have a capability
 * (`payroll: [ACCOUNTING, ADMIN]`). Each is now the capability itself, which is what
 * those lists were always trying to express.
 */
const authRoles = {
  /**
   * Any signed-in account. Held by every role, and the application-wide default for a
   * route that declares nothing.
   *
   * It cannot be an empty array: `hasPermission` reads `[]` as "guests only", so a
   * route defaulting to that would be reachable by exactly the people who are not
   * signed in.
   */
  user: ['app.access'],

  adminConsole: ['admin.console.view'],
  manageUsers: ['users.edit.all'],
  manageRoles: ['users.roles.edit'],
  readFinancials: ['users.financials.read'],
  editFinancials: ['users.financials.edit'],

  // Either scope reaches the board; which closings come back is the API's decision.
  closingEventsView: ['closingBoard.view.all', 'closingBoard.view.own'],
  closingEventsEditProjectedFees: ['closingBoard.fees.projected.edit'],
  closingEventsEditCollectedFees: ['closingBoard.fees.collected.edit'],
  closingEventsCreate: ['closingBoard.event.create'],

  payroll: ['payroll.read.all', 'payroll.read.own'],
  payrollEdit: ['payroll.edit'],

  marketing: ['marketing.forms.read', 'marketing.templates.read'],
  dataDirectory: ['dataDirectory.read'],
  dataDirectoryEdit: ['dataDirectory.edit'],
  analytics: ['analytics.read'],
  dashboard: ['dashboard.read'],
  dataCenter: ['analytics.read', 'dashboard.read'],
  siteSettings: ['settings.site.edit'],

  /**
   * Signed-out visitors only — the login and password screens. An empty array is
   * `hasPermission`'s way of saying that, and is the one place it is correct.
   */
  onlyGuest: []
};

export default authRoles;
